import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

/**
 * OverlayContext - Manages overlay queue to prevent multiple overlays from appearing simultaneously
 * 
 * Features:
 * - Queue-based overlay management
 * - Priority levels (critical, high, normal, low)
 * - Auto-dismiss timers
 * - Prevents UX overload with multiple simultaneous popups
 */

const OverlayContext = createContext();

// Priority levels (higher number = higher priority)
export const OVERLAY_PRIORITY = {
  LOW: 1,        // Non-essential (promotions, tips)
  NORMAL: 2,     // Standard notifications
  HIGH: 3,       // Important user actions
  CRITICAL: 4    // Essential (authentication, errors, consent)
};

export function OverlayProvider({ children }) {
  const [queue, setQueue] = useState([]);
  const [currentOverlay, setCurrentOverlay] = useState(null);
  const dismissTimeoutRef = useRef(null);

  // Process the queue when current overlay is dismissed
  const processQueue = useCallback(() => {
    setQueue(prevQueue => {
      if (prevQueue.length === 0) {
        setCurrentOverlay(null);
        return prevQueue;
      }

      // Sort by priority (highest first) then by timestamp (oldest first)
      const sorted = [...prevQueue].sort((a, b) => {
        if (b.priority !== a.priority) return b.priority - a.priority;
        return a.timestamp - b.timestamp;
      });

      const next = sorted[0];
      setCurrentOverlay(next);
      return sorted.slice(1);
    });
  }, []);

  // Add an overlay to the queue
  const showOverlay = useCallback(({ 
    id, 
    priority = OVERLAY_PRIORITY.NORMAL,
    autoDismissMs = null,
    component = null,
    data = {}
  }) => {
    const newOverlay = {
      id,
      priority,
      autoDismissMs,
      component,
      data,
      timestamp: Date.now()
    };

    // If no current overlay, show immediately if priority is high enough
    if (!currentOverlay) {
      setCurrentOverlay(newOverlay);
      return;
    }

    // If new overlay has higher priority, queue current and show new
    if (priority > currentOverlay.priority) {
      setQueue(prev => [...prev, currentOverlay]);
      setCurrentOverlay(newOverlay);
      return;
    }

    // Otherwise, add to queue
    setQueue(prev => [...prev, newOverlay]);
  }, [currentOverlay]);

  // Dismiss the current overlay
  const dismissOverlay = useCallback((overlayId = null) => {
    if (overlayId && currentOverlay?.id !== overlayId) {
      // Remove from queue if not current
      setQueue(prev => prev.filter(o => o.id !== overlayId));
      return;
    }

    // Clear any auto-dismiss timeout
    if (dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current);
      dismissTimeoutRef.current = null;
    }

    // Process next in queue after a brief delay for smooth transitions
    setTimeout(processQueue, 300);
  }, [currentOverlay, processQueue]);

  // Check if a specific overlay is showing
  const isOverlayActive = useCallback((overlayId) => {
    return currentOverlay?.id === overlayId;
  }, [currentOverlay]);

  // Check if any overlay is showing
  const hasActiveOverlay = currentOverlay !== null;

  // Set up auto-dismiss timer when currentOverlay changes
  useEffect(() => {
    if (dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current);
      dismissTimeoutRef.current = null;
    }

    if (currentOverlay?.autoDismissMs) {
      dismissTimeoutRef.current = setTimeout(() => {
        dismissOverlay(currentOverlay.id);
      }, currentOverlay.autoDismissMs);
    }

    return () => {
      if (dismissTimeoutRef.current) {
        clearTimeout(dismissTimeoutRef.current);
      }
    };
  }, [currentOverlay, dismissOverlay]);

  // Delay non-essential overlays on initial page load
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  
  useEffect(() => {
    // Wait 5 seconds after mount before allowing low-priority overlays
    const timer = setTimeout(() => {
      setIsInitialLoadComplete(true);
    }, 5000);
    
    return () => clearTimeout(timer);
  }, []);

  // Modified showOverlay that respects initial load delay
  const showOverlayWithDelay = useCallback((overlayConfig) => {
    const { priority = OVERLAY_PRIORITY.NORMAL } = overlayConfig;
    
    // Delay low-priority overlays until initial load is complete
    if (priority <= OVERLAY_PRIORITY.LOW && !isInitialLoadComplete) {
      setTimeout(() => showOverlay(overlayConfig), 5000);
      return;
    }
    
    showOverlay(overlayConfig);
  }, [showOverlay, isInitialLoadComplete]);

  const value = {
    currentOverlay,
    showOverlay: showOverlayWithDelay,
    dismissOverlay,
    isOverlayActive,
    hasActiveOverlay,
    queueLength: queue.length,
    OVERLAY_PRIORITY
  };

  return (
    <OverlayContext.Provider value={value}>
      {children}
    </OverlayContext.Provider>
  );
}

export function useOverlay() {
  const context = useContext(OverlayContext);
  if (!context) {
    throw new Error('useOverlay must be used within an OverlayProvider');
  }
  return context;
}

export default OverlayContext;
